<div class="janus4-preview-frame-container">
  <iframe title="Live Preview" name={frameName} bind:this={iframeEl} on:load={handleIframeLoad}></iframe>
  {#if loading}
    <div class="janus4-preview-loading" aria-live="polite">
      <div class="janus4-preview-spinner"></div>
      <span class="janus4-preview-loading-text">Loading preview&hellip;</span>
    </div>
  {/if}
  <form method="POST" target={frameName} bind:this={formEl} style="display: none;" aria-hidden="true">
    <input type="hidden" name="pageData" bind:this={inputEl} />
    <input type="hidden" name="slug" bind:this={slugInput} />
    <input type="hidden" name="section" bind:this={sectionInput} />
    <input type="hidden" name="previewParams" bind:this={previewParamsInput} />
  </form>
</div>

<style>
  .janus4-preview-frame-container {
    position: relative;
    width: 100%;
    height: 100%;
  }

  .janus4-preview-frame-container > iframe {
    display: block;
    width: 100%;
    height: 100%;
    border: none;
    background: white;
  }

  .janus4-preview-loading {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
    background: rgba(255, 255, 255, 0.92);
    z-index: 1;
  }

  .janus4-preview-spinner {
    width: 36px;
    height: 36px;
    border: 3px solid rgba(0, 0, 0, 0.12);
    border-top-color: rgb(0, 122, 255);
    border-radius: 50%;
    animation: janus4-preview-spin 0.8s linear infinite;
  }

  .janus4-preview-loading-text {
    font-family: 'SF UI Display', sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: #555;
  }

  @keyframes janus4-preview-spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .janus4-preview-spinner {
      animation-duration: 2s;
    }
  }</style>

<script lang="ts">// Read-only preview of the current page JSON rendered by the janus4 (Bison)
// engine. The page JSON is delivered by submitting a hidden form (POST,
// target=iframe) to the janus4 deployment's /builder-preview route, which
// runs the JSON through its normal SSR pipeline (upgrade + datasource
// resolution) and renders the result. No postMessage protocol is required.
import { get } from 'svelte/store';
import { onMount, onDestroy } from 'svelte';
import _ from 'lodash';
import 'yinzcam-cards';
import { janus4PreviewEnvironmentStore, getJanus4PreviewBaseUrl } from './Janus4PreviewEnvironment';
import { janus4AvailableSectionsStore, janus4CmsSectionStore, janus4SectionOverrideStore, janus4SectionNamesStore } from './Janus4PreviewSection';
export let pageData = undefined;
// Page identity from the parent CMS: slug -> path0, section -> site-section
// (Contentful + pages env). Path1+ and other editor params come from the
// page JSON's editorParameters (forwarded as previewParams).
export let livePreviewContext = undefined;
const frameName = _.uniqueId('janus4-preview-frame-');
// Safety net: if the iframe load event never arrives (e.g. a network
// failure), don't leave the spinner up forever.
const LOAD_TIMEOUT_MS = 20000;
let iframeEl;
let formEl;
let inputEl;
let slugInput;
let sectionInput;
let previewParamsInput;
let mounted = false;
let lastSubmittedKey = '';
let loading = false;
let everSubmitted = false;
let loadTimeout = null;
function serializePageData() {
    // Strip internal keys the same way PageBuilder does when sending
    // STORE_PAGE back to the parent CMS.
    return JSON.stringify(($pageData || {}), (k, v) => {
        return (k === null || k === void 0 ? void 0 : k.startsWith('__')) ? undefined : v;
    });
}
// The section to preview under. A page authored under a specific section
// (cmsSection is one of the deployment's sections) always previews under it.
// Otherwise (shared/default-env page) the persisted override applies, as long
// as it's a section this deployment actually reports.
function computeEffectiveSection(cmsSection) {
    const available = get(janus4AvailableSectionsStore) || [];
    const isSectionPage = !!cmsSection && available.indexOf(cmsSection) !== -1;
    if (isSectionPage) {
        return cmsSection;
    }
    const override = get(janus4SectionOverrideStore) || '';
    if (override && available.indexOf(override) !== -1) {
        return override;
    }
    return cmsSection;
}
function handleMessageFromFrame(e) {
    if (!iframeEl || e.source !== iframeEl.contentWindow) {
        return;
    }
    const expectedOrigin = getJanus4PreviewBaseUrl(get(janus4PreviewEnvironmentStore));
    if (e.origin !== expectedOrigin) {
        return;
    }
    const payload = e.data;
    if (typeof payload !== 'string' || payload.charAt(0) !== '{') {
        return;
    }
    let message;
    try {
        message = JSON.parse(payload);
    }
    catch (err) {
        return;
    }
    if (message.type === 'BUILDER_PREVIEW_SECTIONS' && Array.isArray(message.sections)) {
        janus4AvailableSectionsStore.set(message.sections.filter((s) => typeof s === 'string'));
    }
}
function submit() {
    if (!mounted || !formEl || !inputEl) {
        return;
    }
    const actionUrl = `${getJanus4PreviewBaseUrl(get(janus4PreviewEnvironmentStore))}/builder-preview?x-yinzcam-no-edge-blocks=1`;
    const payload = serializePageData();
    const ctx = (livePreviewContext) ? (get(livePreviewContext) || {}) : {};
    const slug = ctx.slug || '';
    const section = computeEffectiveSection(ctx.section || '');
    // Path variables and other editor params come from the page JSON's
    // editorParameters (path0/path1/path2/query/gameId/custom). The CMS slug
    // overrides path0 on the janus4 side.
    const editorParameters = ($pageData && $pageData.editorParameters) ? $pageData.editorParameters : {};
    const previewParams = JSON.stringify(editorParameters);
    const key = `${actionUrl}\n${section}\n${slug}\n${previewParams}\n${payload}`;
    if (key === lastSubmittedKey) {
        return;
    }
    lastSubmittedKey = key;
    formEl.action = actionUrl;
    inputEl.value = payload;
    if (slugInput)
        slugInput.value = slug;
    if (sectionInput)
        sectionInput.value = section;
    if (previewParamsInput)
        previewParamsInput.value = previewParams;
    everSubmitted = true;
    loading = true;
    if (loadTimeout)
        clearTimeout(loadTimeout);
    loadTimeout = setTimeout(() => { loading = false; }, LOAD_TIMEOUT_MS);
    formEl.submit();
}
function handleIframeLoad() {
    // Ignore the iframe's initial about:blank load, which fires before any
    // submission. Every load after the first submit is a real SSR response.
    if (!everSubmitted) {
        return;
    }
    loading = false;
    if (loadTimeout) {
        clearTimeout(loadTimeout);
        loadTimeout = null;
    }
}
const debouncedSubmit = _.debounce(submit, 1000);
// Reload the preview immediately when the environment or section-override
// selection changes, when the reported sections arrive (so a persisted
// override can take effect), and (debounced) when the page data changes.
$: if (mounted && $janus4PreviewEnvironmentStore) {
    submit();
}
$: if (mounted && $janus4SectionOverrideStore !== undefined) {
    submit();
}
$: if (mounted && $janus4AvailableSectionsStore) {
    submit();
}
$: if (mounted && $pageData) {
    debouncedSubmit();
}
let unsubscribeContext = null;
onMount(() => {
    mounted = true;
    window.addEventListener('message', handleMessageFromFrame);
    // Keep the shared CMS-section store in sync so the control bar can decide
    // whether to offer the section-override dropdown.
    if (livePreviewContext) {
        unsubscribeContext = livePreviewContext.subscribe((ctx) => {
            janus4CmsSectionStore.set((ctx === null || ctx === void 0 ? void 0 : ctx.section) || '');
            janus4SectionNamesStore.set((ctx === null || ctx === void 0 ? void 0 : ctx.environmentNames) || {});
        });
    }
    submit();
});
onDestroy(() => {
    window.removeEventListener('message', handleMessageFromFrame);
    if (unsubscribeContext)
        unsubscribeContext();
    debouncedSubmit.cancel();
    if (loadTimeout)
        clearTimeout(loadTimeout);
});
</script>
